import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { OutletFormDialog } from "@/components/feature/media/outlet-form-dialog";
import { useOutletKinds } from "@/hooks/use-outlet-kinds";
import { media, type OutletOut } from "@/lib/api";

export const Route = createFileRoute("/_auth/media/outlets")({
  component: MediaOutlets,
});

function MediaOutlets() {
  const qc = useQueryClient();
  const { labels: kindLabels } = useOutletKinds();
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<OutletOut | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["media", "outlets"],
    queryFn: () => media.listOutlets({ limit: 500 }),
  });

  const remove = useMutation({
    mutationFn: (id: number) => media.deleteOutlet(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "outlets"] });
      toast.success("Medio desactivado");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <Skeleton className="h-40" />;

  // Agrupar por kind
  const groups: Record<string, OutletOut[]> = {};
  for (const o of data ?? []) {
    (groups[o.kind] ||= []).push(o);
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs text-ink-500">
          {(data ?? []).length} medios · {(data ?? []).filter(o => o.is_active).length} activos
        </div>
        <Button onClick={() => setShowCreate(true)}>
          <Plus size={14} /> Nuevo medio
        </Button>
      </div>

      <div className="space-y-6">
        {Object.entries(groups).map(([kind, list]) => (
          <div key={kind}>
            <h3 className="text-sm font-semibold text-ink-700 mb-2 uppercase tracking-wide">
              {kindLabels[kind] ?? kind}
              <span className="ml-2 text-ink-400 normal-case font-normal">({list.length})</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {list.map(o => (
                <Card key={o.id} className={!o.is_active ? "opacity-50" : ""}>
                  <CardContent className="py-3">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">{o.name}</div>
                        {o.coverage && (
                          <div className="text-xs text-ink-500 mt-1">Cobertura: {o.coverage}</div>
                        )}
                        {o.frequency_mhz && (
                          <div className="text-xs text-ink-500">{o.frequency_mhz} MHz</div>
                        )}
                        {o.circulation && (
                          <div className="text-xs text-ink-500">Tiraje: {o.circulation.toLocaleString("es-PE")}</div>
                        )}
                      </div>
                      <Badge variant={o.is_active ? "default" : "outline"}>
                        {o.is_active ? "Activo" : "Inactivo"}
                      </Badge>
                    </div>
                    {o.accepts_formats?.length > 0 && (
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {o.accepts_formats.map(f => (
                          <span key={f} className="text-[10px] uppercase bg-ink-100 dark:bg-ink-800 px-1.5 py-0.5 rounded">
                            {f}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Tarifario resumido */}
                    {(o.rate_base_amount || o.rate_per_word || o.rate_per_second || o.rate_per_repetition) && (
                      <div className="mt-2 p-2 rounded bg-emerald-50/60 dark:bg-emerald-900/20 text-[10px] space-y-0.5">
                        <div className="font-semibold text-emerald-700 dark:text-emerald-300">
                          💰 {o.rate_currency} ·
                        </div>
                        {o.rate_base_amount != null && <div>Base: {Number(o.rate_base_amount).toFixed(2)}</div>}
                        {o.rate_per_word != null && <div>/palabra: {Number(o.rate_per_word).toFixed(4)}</div>}
                        {o.rate_per_second != null && <div>/segundo: {Number(o.rate_per_second).toFixed(4)}</div>}
                        {o.rate_per_minute != null && <div>/minuto: {Number(o.rate_per_minute).toFixed(4)}</div>}
                        {o.rate_per_repetition != null && <div>/repetición: {Number(o.rate_per_repetition).toFixed(4)}</div>}
                        {o.rate_apply_repetitions && <div className="italic">× repeticiones</div>}
                      </div>
                    )}
                    <div className="flex gap-1 mt-3 pt-2 border-t border-ink-100 dark:border-ink-800">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(o)} className="flex-1">
                        <Pencil size={12} /> Editar
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove.mutate(o.id)} title="Desactivar">
                        <Trash2 size={12} className="text-bad" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
        {!(data ?? []).length && (
          <div className="text-center py-12 text-ink-500 border-2 border-dashed border-ink-200 rounded-lg">
            Sin medios. Crea el primero o seedea referenciales desde el panel admin.
          </div>
        )}
      </div>

      <OutletFormDialog open={showCreate} onOpenChange={setShowCreate} outlet={null} />
      <OutletFormDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} outlet={editing} />
    </>
  );
}
